import React, { useState, useEffect } from 'react'

// Third Party
import { v4 as uuidv4 } from 'uuid'

// COMPONENTS
import SidebarSection from '../../../../components/SidebarSection'
import OutlinedInput from '@mui/material/OutlinedInput'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'

// CSS
import '../../../../App.css'

// MUI
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Grid,
    Chip,
    IconButton,
    Menu,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { Description, LibraryBooks, Storage } from '@mui/icons-material'

// Contexts
import { useDatabase } from '../../../../contexts/DatabaseContext'
import { useQuery } from '../../../../contexts/QueryContext'
import { useDocumentType } from '../../../../contexts/DocumentTypeContext'

// Utils
import { capitalizeWord, formatSectionTextSync } from '../../../../utils/sectionExtract'

// FUNCTIONS
function extractManualContextFromXML(mrt_xml) {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(mrt_xml, 'text/xml')
    // const manualContext = xmlDoc.getElementsByTagName('template_text')
    const manualContext = xmlDoc.documentElement.textContent
    return manualContext
}

function formatElementValues(value, isMultipleWords = false, delimiter = '') {
    console.log('value: ', value)
    if (
        value === 'copy/paste' ||
        value === 'Copy/Paste' ||
        value === 'Copy/paste'
    ) {
        return 'Copy/Paste'
    } else if (
        value === 'Summarize TFL' ||
        value === 'Summarize tfl' ||
        value === 'summarize tfl'
    ) {
        return 'Summarize TFL'
    } else if (
        value === 'Summarize' ||
        value === 'summarize'
    ) {
        return 'Summarize'
    } else if (isMultipleWords) {
        return value
            .split(delimiter)
            .map((word) => {
                if (word === 'tfl' || word === 'TFL') {
                    return 'TFL'
                }
                return (
                    word.trim().charAt(0).toUpperCase() +
                    word.slice(1).toLowerCase()
                )
            })
            .join(' ')
    } else {
        return (
            value.trim().charAt(0).toUpperCase() + value.slice(1).toLowerCase()
        )
    }
}

function formatDocumentType(documentType) {
    switch (documentType) {
        case 'Protocol':
            return 'Protocol'
        case 'SAP':
            return 'Statistical Analysis Plan'
        case 'CSR':
            return 'Clinical Study Report'
        case 'TOL':
            return 'Table of Lists'
        case 'TFL':
            return 'Tables, Figures, and Listings'
        default:
            return documentType
    }
}

const TemplateSidebarSubSection = ({
    title,
    onEdit = null,
    onSave = null,
    onExit = null,
    isEditing = false,
    children,
}) => {
    return (
        <SidebarSection
            level={1}
            title={title}
            onEdit={onEdit}
            onSave={onSave}
            onExit={onExit}
            isEditing={isEditing}
        >
            {children}
        </SidebarSection>
    )
}

const tlf_file_extensions = ['.rtf', '.html', '.xlsx', '.pdf', '.docx']

function formatSourceName(source) {
    if (source === 'Protocol') {
        return source + '.docx'
    } else if (source === 'CRF') {
        return source + '.pdf'
    } else if (source === 'SAP') {
        return source + '.pdf'
    } else {
        return (
            source +
            tlf_file_extensions[
                Math.floor(Math.random() * tlf_file_extensions.length)
            ]
        )
    }
}

const generateSources = (element) => {
    return [
        {
            id: uuidv4(),
            source: element.source_location.source,
            section: element.source_location.location,
        },
    ]
}

const source_data_types = [
    {
        id: uuidv4(),
        name: 'Document',
        icon: <Description />,
    },
    {
        id: uuidv4(),
        name: 'Component Library',
        icon: <LibraryBooks />,
    },
    {
        id: uuidv4(),
        name: 'Database',
        icon: <Storage />,
    },
]

const document_rule_types = [
    {
        id: uuidv4(),
        name: 'Summarize',
    },
    {
        id: uuidv4(),
        name: 'Copy/Paste',
    },
    {
        id: uuidv4(),
        name: 'Summarize TFL',
    },
    {
        id: uuidv4(),
        name: 'Template Text',
    },
    {
        id: uuidv4(),
        name: 'Fill in the Blank',
    },
]

const component_rule_types = [
    {
        id: uuidv4(),
        name: 'Fill in the Blank',
    },
    {
        id: uuidv4(),
        name: 'Copy/Paste',
    },
    {
        id: uuidv4(),
        name: 'Select',
    },
]

const database_rule_types = [
    {
        id: uuidv4(),
        name: 'Summarize',
    },
    {
        id: uuidv4(),
        name: 'Insert Table',
    },
    {
        id: uuidv4(),
        name: 'Fill In the Blank',
    },
]

export function FramedText({ text }) {
    return (
        <FramedContent>
            <Typography variant="body1" className="secondary-text">
                {text}
            </Typography>
        </FramedContent>
    )
}

export function FramedContent({
    children,
    borderColor = 'rgba(0, 0, 0, 0.23)',
    borderWidth = 1,
    borderStyle = 'solid',
    padding = 10,
    maxHeight = 200,
    scrollable = true,
}) {
    const containerStyle = {
        border: `${borderWidth}px ${borderStyle} ${borderColor}`,
        padding: `${padding}px`,
        borderRadius: '4px',
        display: 'inline-block', // Adjust as needed (e.g., 'block' or 'inline-flex')
        maxHeight: `${maxHeight}px`,
        overflow: scrollable ? 'auto' : 'hidden',
        whiteSpace: 'pre-wrap',
        width: '100%',
    }

    return <Box sx={containerStyle}>{children}</Box>
}
const DatabaseComponent = ({ element }) => {
    const { databases } = useDatabase()
    const { queries, setQueries } = useQuery()

    const [ruleChipLabel, setRuleChipLabel] = useState(database_rule_types[0])

    const [selectedVariableOptionsMap, setSelectedVariableOptionsMap] =
        useState({})

    const [database, setDatabase] = useState(databases[0])
    const [query, setQuery] = useState(queries[0])

    const [anchorElRule, setAnchorElRule] = useState(null)
    const openRuleMenu = Boolean(anchorElRule)

    const handleOpenRuleMenu = (event) => {
        setAnchorElRule(event.currentTarget)
        event.stopPropagation()
    }

    const handleCloseRuleMenu = () => {
        setAnchorElRule(null)
    }

    const handleRuleMenuItemClick = (event, type) => {
        setRuleChipLabel(type)
        handleCloseRuleMenu()
        event.stopPropagation()
    }

    const handleChange = (event) => {
        const {
            target: { value },
        } = event
        setQuery(value)
        // Reset selections when query changes
        setSelectedVariableOptionsMap({})
    }

    const handleVariableChange = (
        variableId,
        variableRepresentation,
        variableValue,
        value
    ) => {
        console.log('value: ', value)
        console.log('variableValue: ', variableValue)
        // Update the selections for this specific variable
        setSelectedVariableOptionsMap((prevState) => ({
            ...prevState,
            [variableId]: value,
        }))

        const checkedValue =
            value.length > 0
                ? value.map((item) => item.label).join(',')
                : variableRepresentation

        console.log(
            'selectedVariableOptionsMap[variableId]: ',
            selectedVariableOptionsMap[variableId]
        )

        const textToReplace =
            selectedVariableOptionsMap[variableId] !== undefined &&
            selectedVariableOptionsMap[variableId].length > 0
                ? new RegExp(
                      `\\b${selectedVariableOptionsMap[variableId].map(
                          (item) => item.label
                      ).join(',')}\\b`,
                      'g'
                  )
                : variableRepresentation

        console.log('textToReplace: ', textToReplace)

        const filledInQueryString = query.query_sql.replace(
            textToReplace,
            checkedValue
        )

        const updatedQuery = {
            ...query,
            query_sql: filledInQueryString,
        }

        setQueries(
            queries.map((q) => {
                if (q.id === updatedQuery.id) {
                    return updatedQuery
                }
                return q
            })
        )
        setQuery(updatedQuery)
    }

    const ITEM_HEIGHT = 48
    const ITEM_PADDING_TOP = 8
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    }

    return (
        <>
            <Box className="vertical-container" sx={{ gap: 1, width: '100%' }}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Rule Type
                </Typography>
                <Chip
                    label={formatElementValues(ruleChipLabel.name, true, ' ')}
                    onClick={(event) => {
                        handleOpenRuleMenu(event)
                    }}
                    size="large"
                    key={ruleChipLabel.id}
                    color="primary"
                />
                <Menu
                    id="add-database-rule-chip-menu"
                    anchorEl={anchorElRule}
                    open={openRuleMenu}
                    onClose={handleCloseRuleMenu}
                    MenuListProps={{
                        'aria-labelledby': 'add-database-rule-chip-button',
                    }}
                >
                    {database_rule_types.map((database_rule_type) => (
                        <MenuItem
                            key={database_rule_type.id}
                            onClick={(event) => {
                                handleRuleMenuItemClick(
                                    event,
                                    database_rule_type
                                )
                                event.stopPropagation()
                            }}
                        >
                            {formatElementValues(
                                database_rule_type.name,
                                true,
                                ' '
                            )}
                        </MenuItem>
                    ))}
                </Menu>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Database
                </Typography>
                <FormControl
                    sx={{ my: 1, minWidth: 120, width: '100%' }}
                    size="small"
                >
                    <InputLabel id="demo-select-small-label">Query</InputLabel>
                    <Select
                        labelId="demo-select-small-label"
                        id="demo-select-small"
                        value={query}
                        label="Query"
                        fullWidth
                        onChange={handleChange}
                        onClick={(event) => {
                            event.stopPropagation()
                        }}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {queries.map((query) => (
                            <MenuItem key={query.id} value={query}>
                                {query.query_name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {query.variableList.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            my: 1,
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            className="secondary-text"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {query.variableList.length === 1
                                ? 'Variable'
                                : 'Variables'}
                        </Typography>
                        <Grid container spacing={2} sx={{ width: '100%' }}>
                            {query.variableList.map((variable) => (
                                <Grid
                                    item
                                    key={variable.id}
                                    size={{ xs: 12, md: 6 }}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                        <Select
                                            multiple
                                            displayEmpty
                                            
                                            value={
                                                selectedVariableOptionsMap[
                                                    variable.id
                                                ] || []
                                            }
                                            onChange={(event) => {
                                                handleVariableChange(
                                                    variable.id,
                                                    variable.representation,
                                                    variable.value,
                                                    event.target.value
                                                )
                                                event.stopPropagation()
                                            }}
                                            size="small"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                            }}
                                            input={
                                                <OutlinedInput
                                                    
                                                    sx={{
                                                        height: '100%',
                                                        width: '100%',
                                                    }}
                                                />
                                            }
                                            renderValue={(selected) => {
                                                if (selected.length === 0) {
                                                    return (
                                                        <em
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {capitalizeWord(
                                                                variable.label
                                                            )}
                                                        </em>
                                                    )
                                                }

                                                const selectedValues = selected.map(
                                                    (item) => item.label
                                                )
                                                return selectedValues.join(', ')
                                            }}
                                            MenuProps={MenuProps}
                                        >
                                            {variable.value.map((value) => (
                                                <MenuItem
                                                    key={value.id}
                                                    value={value}
                                                >
                                                    <Checkbox
                                                        checked={(
                                                            selectedVariableOptionsMap[
                                                                variable.id
                                                            ] || []
                                                        ).some(
                                                            (item) =>
                                                                item.id === value.id
                                                        )}
                                                    />
                                                    <ListItemText
                                                        primary={value.label}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Preview
                </Typography>
                <FramedText
                    text={query.query_sql}
                    borderColor="rgba(0, 0, 0, 0.23)"
                    borderWidth={1}
                    borderStyle="solid"
                    padding={10}
                />
            </Box>
        </> 
    )
}

const RenderSourceDataComponent = ({
    element,
    sourceDataType,
    components,
    templateData,
    setTemplateData,
    section,
}) => {
    const { documentTypes } = useDocumentType()

    switch (sourceDataType) {
        case 'Document':
            return (
                <DocumentTypeElement
                    element={element}
                    templateData={templateData}
                    setTemplateData={setTemplateData}
                    documentTypes={documentTypes}
                />
            )
        case 'Component Library':
            return (
                <ComponentLibraryElement
                    elementData={element}
                    components={components}
                    templateData={templateData}
                    setTemplateData={setTemplateData}
                    section={section}
                />
            )
        case 'Database':
            return <DatabaseComponent element={element} />
        default:
            return null
    }
}

function ElementContainer({ children }) {
    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minHeight: '200px',
                }}
            >
                <Box
                    className="elements-background"
                    sx={{ minHeight: '200px' }}
                >
                    <Box className="draggable-header">
                        <Box
                            className="elements-container"
                            sx={{ justifyContent: 'center' }}
                        >
                            {children}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
function ComponentLibraryElement({
    elementData,
    components,
    templateData,
    setTemplateData,
    section,
}) {
    console.log('components: ', components)
    console.log('elementData: ', elementData)
    console.log('section: ', section)

    const section_name = templateData.find((section) =>
        section.fixed_elements.find((element) => element.id === elementData.id)
    ).section_name

    console.log('section_name: ', section_name)

    const emptyComponent = {
        id: '',
        value: ' ',
        component: '',
        section_name: '',
    }

    console.log("elementData.rule: ", elementData.rule)
    console.log("component_rule_types[0]: ", component_rule_types[0])
    const [ruleChipLabel, setRuleChipLabel] = useState(elementData.rule === 'Copy/Paste' ? component_rule_types[1] : component_rule_types[0])

    const [numberOfOptions, setNumberOfOptions] = useState(3)

    const [anchorElRule, setAnchorElRule] = useState(null)
    const openRuleMenu = Boolean(anchorElRule)

    const [optionComponents, setOptionComponents] = useState([
        { label: 'Option 1', component: emptyComponent, id: uuidv4() },
        { label: 'Option 2', component: emptyComponent, id: uuidv4() },
        { label: 'Option 3', component: emptyComponent, id: uuidv4() },
    ])

    const [element, setElement] = useState(elementData)

    const handleOpenRuleMenu = (event) => {
        setAnchorElRule(event.currentTarget)
        event.stopPropagation()
    }

    const handleCloseRuleMenu = () => {
        setAnchorElRule(null)
    }

    const handleRuleMenuItemClick = (event, type) => {
        if (ruleChipLabel.source !== type) {
            setRuleChipLabel(type)
            const updatedTemplateData = templateData.map((section) => {
                const updatedElements = section.fixed_elements.map(
                    (currentElement) => {
                        if (currentElement.id === element.id) {
                            const updatedElement = {
                                ...currentElement,
                                rule: type.name,
                            }
                            setElement(updatedElement)
                            return updatedElement
                        }
                        return currentElement
                    }
                )
                return {
                    ...section,
                    fixed_elements: updatedElements,
                }
            })
        }
        handleCloseRuleMenu()
        event.stopPropagation()
    }

    const renderNumberOfOptions = (numberOfOptions) => {
        return (
            <Box>
                {Array.from(Array(numberOfOptions)).map((_, index) => {
                    return (
                        <Box
                            key={index}
                            className="vertical-container"
                            sx={{
                                gap: 1,
                                overflow: 'wrap',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                maxWidth: '500px',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 'bold' }}
                            >
                                {`Option ${index + 1}`}
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={optionComponents[index].component}
                                    onChange={(event) => {
                                        setOptionComponents(
                                            optionComponents.map(
                                                (optionComponent, i) => {
                                                    if (i === index) {
                                                        return {
                                                            ...optionComponent,
                                                            component:
                                                                event.target
                                                                    .value
                                                        }
                                                    }
                                                    return optionComponent
                                                }
                                            )
                                        )
                                    }}
                                    size="small"
                                    fullWidth
                                    sx={{
                                        overflow: 'wrap',
                                        whiteSpace: 'wrap',
                                        maxWidth: '100%',
                                        maxHeight: '200px',
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                    }}
                                    maxRows={10}
                                >
                                    {components.map((component) => (
                                        <MenuItem
                                            key={component.id}
                                            value={component}
                                            sx={{
                                                overflow: 'wrap',
                                                whiteSpace: 'wrap',
                                                maxWidth: '100%',
                                            }}
                                        >
                                            {console.log('component: ', component)}
                                            {component.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {optionComponents[index].component.component && (
                                <>
                                    <Typography
                                        variant="subtitle2"
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        Text
                                    </Typography>
                                    {console.log('optionComponents[index].component: ', optionComponents[index].component)}
                                    <FramedText
                                        text={
                                            optionComponents[index].component.component
                                        }
                                        borderColor="rgba(0, 0, 0, 0.23)"
                                        borderWidth={1}
                                        borderStyle="solid"
                                        padding={10}
                                    />
                                </>
                            )}
                        </Box>
                    )
                })}
            </Box>
        )
    }

    return (
        // <ElementContainer>
        <>
            <Box className="vertical-container" sx={{ gap: 1, width: '100%' }}>
                <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 'bold' }}
                >
                    Rule Type
                </Typography>
                <Chip
                    label={formatElementValues(ruleChipLabel.name, true, ' ')}
                    onClick={(event) => {
                        handleOpenRuleMenu(event)
                    }}
                    size="large"
                    key={ruleChipLabel.id}
                    color="primary"
                />
                <Menu
                    id="add-chip-menu"
                    anchorEl={anchorElRule}
                    open={openRuleMenu}
                    onClose={handleCloseRuleMenu}
                    MenuListProps={{
                        'aria-labelledby': 'add-chip-button',
                    }}
                >
                    {component_rule_types.map((component_rule_type) => (
                        <MenuItem
                            key={component_rule_type.id}
                            onClick={(event) => {
                                handleRuleMenuItemClick(
                                    event,
                                    component_rule_type
                                )
                                console.log(
                                    'component_rule_type: ',
                                    ruleChipLabel
                                )
                                event.stopPropagation()
                            }}
                        >
                            {formatElementValues(
                                component_rule_type.name,
                                true,
                                ' '
                            )}
                        </MenuItem>
                    ))}
                </Menu>
                {ruleChipLabel.name === 'Select' ? (
                    <Box sx={{ width: '100%' }}>
                        <Box
                            className="horizontal-container"
                            sx={{
                                display: 'flex',
                                gap: 1,
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Number of Options:
                            </Typography>
                            <Select
                                value={numberOfOptions}
                                onChange={(event) => {
                                    setNumberOfOptions(event.target.value)
                                    event.stopPropagation()
                                }}
                                size="small"
                            >
                                <MenuItem value={0}>
                                    <em>None</em>
                                </MenuItem>
                                <MenuItem value={1}>1</MenuItem>
                                <MenuItem value={2}>2</MenuItem>
                                <MenuItem value={3}>3</MenuItem>
                            </Select>
                        </Box>
                        {renderNumberOfOptions(numberOfOptions)}
                    </Box>
                ) : (
                    <Box
                        className="vertical-container"
                        sx={{
                            gap: 1,
                            overflow: 'wrap',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            maxWidth: '500px',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 'bold' }}
                        >
                            Component
                        </Typography>
                        <FormControl fullWidth>
                            {console.log('optionComponents: ', optionComponents)}
                            {console.log('optionComponents[0]: ', optionComponents[0])}
                            {console.log('optionComponents[0].component: ', optionComponents[0].component)} 
                            <Select
                                value={optionComponents[0].component}
                                onChange={(event) => {
                                    setOptionComponents(
                                        optionComponents.map(
                                            (optionComponent, i) => {
                                                console.log(
                                                    'event: ',
                                                    event.target.value
                                                )
                                                if (i === 0) {
                                                    return {
                                                        ...optionComponent,
                                                        component:
                                                            event.target.value,
                                                    }
                                                }
                                                return optionComponent
                                            }
                                        )
                                    )
                                }}
                                size="small"
                                fullWidth
                                sx={{
                                    overflow: 'wrap',
                                    whiteSpace: 'wrap',
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                }}
                                onClick={(event) => {
                                    event.stopPropagation()
                                }}
                                maxRows={10}
                            >
                                {components.map((component) => (
                                    <MenuItem
                                        key={component.id}
                                        value={component}
                                        sx={{
                                            overflow: 'wrap',
                                            whiteSpace: 'wrap',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {component.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {optionComponents[0].component.component && (
                            <>
                                <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Text
                                </Typography>
                                <FramedText
                                    text={optionComponents[0].component.component}
                                    borderColor="rgba(0, 0, 0, 0.23)"
                                    borderWidth={1}
                                    borderStyle="solid"
                                    padding={10}
                                />
                            </>
                        )}
                    </Box>
                )}
                <Box sx={{ width: '100%' }}>
                    <Typography variant="subtitle2"></Typography>
                </Box>
            </Box>
        </>
    )
}

function DocumentTypeElement({
    element,
    templateData,
    setTemplateData,
    documentTypes,
}) {
    function getSectionType(element) {
        return {
            source: documentTypes.find((documentType) => {
                return (
                    documentType.document_type ===
                    element.source_location.source
                )
            }),
            location: element.source_location.location,
            id: uuidv4(),
        }
    }

    const sectionType = getSectionType(element)
    console.log('sectionType: ', sectionType)

    const [ruleChipLabel, setRuleChipLabel] = useState(element)
    const [sourceChipLabel, setSourceChipLabel] = useState(
        sectionType ? [sectionType] : []
    )

    const [sourceLocationChipPairs, setSourceLocationChipPairs] = useState(
        sectionType ? [sectionType] : []
    )

    const [selectedPair, setSelectedPair] = useState(
        sectionType ? sectionType : {}
    )

    useEffect(() => {
        setSelectedPair(
            sourceLocationChipPairs.find((pair) => {
                return (
                    pair.source.document_type === element.source_location.source
                )
            })
        )
    }, [])

    const [documentType, setDocumentType] = useState(
        documentTypes.find((documentType) => {
            return (
                documentType.document_type ===
                formatDocumentType(element.source_location.source)
            )
        })
    )

    const [anchorElSource, setAnchorElSource] = useState(null)
    const [anchorElRule, setAnchorElRule] = useState(null)
    const [anchorElSection, setAnchorElSection] = useState(null)

    const openSourceMenu = Boolean(anchorElSource)
    const openRuleMenu = Boolean(anchorElRule)
    const openSectionMenu = Boolean(anchorElSection)

    const handleOpenSourceMenu = (event) => {
        setAnchorElSource(event.currentTarget)
        event.stopPropagation()
    }

    const handleCloseSourceMenu = () => {
        setAnchorElSource(null)
    }

    const handleOpenRuleMenu = (event) => {
        setAnchorElRule(event.currentTarget)
        event.stopPropagation()
    }

    const handleCloseRuleMenu = () => {
        setAnchorElRule(null)
    }

    const handleOpenSectionMenu = (event, pair) => {
        setSelectedPair(pair)
        setAnchorElSection(event.currentTarget)
        event.stopPropagation()
    }

    const handleCloseSectionMenu = () => {
        setAnchorElSection(null)
    }

    const handleSourceMenuItemClick = (event, type) => {
        const typeWithUUID = {
            id: uuidv4(),
            source: type,
            location: formatSectionTextSync(type.sections[0].name),
        }
        setDocumentType(type)
        setSourceLocationChipPairs([...sourceLocationChipPairs, typeWithUUID])
        if (sourceChipLabel.source !== typeWithUUID.source) {
            setSourceChipLabel([...sourceChipLabel, typeWithUUID])
        }
        handleCloseSourceMenu()
        event.stopPropagation()
    }

    const handleSectionMenuItemClick = (event, type, pair) => {
        const updatedPair = {
            ...pair,
            location: formatSectionTextSync(type.name),
        }

        setSourceLocationChipPairs(
            sourceLocationChipPairs.map((sourcePair) => {
                if (sourcePair.id === pair.id) {
                    return updatedPair
                } else {
                    return sourcePair
                }
            })
        )

        setSelectedPair(updatedPair)

        handleCloseSectionMenu()
        event.stopPropagation()
    }

    const handleRuleMenuItemClick = (event, type) => {
        console.log('typeof type: ', typeof type)
        console.log('type: ', type)
        console.log('ruleChipLabel: ', ruleChipLabel)
        console.log('ruleChipLabel.rule: ', ruleChipLabel.rule)
        if (ruleChipLabel.rule !== type) {
            ruleChipLabel.rule = type.name
            setRuleChipLabel(ruleChipLabel)
        }
        handleCloseRuleMenu()
        event.stopPropagation()
    }

    return (
        <Box
            sx={{
                width: '100%',
            }}
        >
            {console.log('ruleChipLabel: ', ruleChipLabel)}
            <Box className="vertical-container" sx={{ gap: 1, width: '100%' }}>
                <Typography
                    variant="subtitle1"
                    // className="secondary-text"
                    style={{ fontWeight: 'bold' }}
                >
                    Rule Type
                </Typography>
                <Chip
                    label={formatElementValues(ruleChipLabel.rule, true, '_')}
                    onClick={(event) => {
                        handleOpenRuleMenu(event)
                    }}
                    size="large"
                    key={ruleChipLabel.id}
                    color="primary"
                />
                <Menu
                    id="add-chip-menu"
                    anchorEl={anchorElRule}
                    open={openRuleMenu}
                    onClose={handleCloseRuleMenu}
                    MenuListProps={{
                        'aria-labelledby': 'add-chip-button',
                    }}
                >
                    {document_rule_types.map((document_rule_type) => (
                        <MenuItem
                            key={document_rule_type.id}
                            onClick={(event) =>
                                handleRuleMenuItemClick(
                                    event,
                                    document_rule_type
                                )
                            }
                        >
                            {formatElementValues(
                                document_rule_type.name,
                                true,
                                ' '
                            )}
                        </MenuItem>
                    ))}
                </Menu>
                <Grid
                    container
                    spacing={2}
                    sx={{
                        width: '100%',
                    }}
                >
                    <Grid
                        item
                        size={{ xs: 12, md: 6 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Box
                            className="vertical-container"
                            sx={{
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 'bold' }}
                            >
                                Source
                            </Typography>

                            {sourceLocationChipPairs.length > 0 &&
                                sourceLocationChipPairs.map((pair) => (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 0.5,
                                            mt: 0.5,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '165px',
                                        }}
                                    >
                                        {console.log('source: ', pair)}
                                        <Tooltip
                                            title={pair.source.document_type}
                                        >
                                            <Chip
                                                label={
                                                    pair.source.document_type
                                                }
                                                onClick={(event) => {
                                                    handleOpenSourceMenu(event)
                                                    event.stopPropagation()
                                                }}
                                                size="large"
                                                key={pair.id}
                                            />
                                        </Tooltip>
                                    </Box>
                                ))}
                            <IconButton
                                size="small"
                                onClick={handleOpenSourceMenu}
                                aria-label="add tag"
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                            <Menu
                                id="add-chip-menu"
                                anchorEl={anchorElSource}
                                open={openSourceMenu}
                                onClose={handleCloseSourceMenu}
                                MenuListProps={{
                                    'aria-labelledby': 'add-chip-button',
                                }}
                            >
                                {documentTypes.map((documentType) => (
                                    <MenuItem
                                        key={documentType.id}
                                        onClick={(event) =>
                                            handleSourceMenuItemClick(
                                                event,
                                                documentType
                                            )
                                        }
                                    >
                                        {documentType.document_type}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        size={{ xs: 12, md: 6 }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <Box
                            className="vertical-container"
                            sx={{
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 'bold' }}
                            >
                                Section
                            </Typography>
                            {sourceLocationChipPairs.length > 0 &&
                                sourceLocationChipPairs.map((pair) => (
                                    <Box
                                        key={pair.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            flexWrap: 'nowrap',
                                            gap: 0.5,
                                            mt: 0.5,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '165px',
                                        }}
                                    >
                                        <Tooltip
                                            title={formatSectionTextSync(
                                                pair.location
                                            )}
                                        >
                                            <Chip
                                                label={formatSectionTextSync(
                                                    pair.location
                                                )}
                                                onClick={(event) => {
                                                    handleOpenSectionMenu(
                                                        event,
                                                        pair
                                                    )
                                                    event.stopPropagation()
                                                }}
                                                size="large"
                                            />
                                        </Tooltip>
                                    </Box>
                                ))}
                            <Menu
                                id="add-chip-menu"
                                anchorEl={anchorElSection}
                                open={openSectionMenu}
                                onClose={handleCloseSectionMenu}
                                MenuListProps={{
                                    'aria-labelledby': 'add-chip-button',
                                }}
                            >
                                {selectedPair.source.sections.map((section) => (
                                    <MenuItem
                                        key={section.id}
                                        onClick={(event) => {
                                            handleSectionMenuItemClick(
                                                event,
                                                section,
                                                selectedPair
                                            )
                                        }}
                                    >
                                        {section.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

const DescriptionContent = ({ data }) => {
    return (
        <Box>
            <Typography variant="subtitle2" className="secondary-text">
                {data}
            </Typography>
        </Box>
    )
}

const FixedElementsComponent = ({
    element,
    section,
    components,
    templateData,
    setTemplateData,
}) => {
    console.log('element: ', element)
    const [sourceDataType, setSourceDataType] = useState(
        element.source_type 
            ? element.source_type 
            : element.source_location 
                ? source_data_types[0].name 
                : ''
    )

    const handleChange = (event, value) => {
        if (value !== null) {
            setSourceDataType(typeof value === 'string' ? value : value)
        }
    }

    function formatSourceDataType(sourceDataType) {
        return sourceDataType.name.split(' ').join('\n')
    }

    return (
        <>
            <ElementContainer>
                <RenderSourceDataComponent
                    element={element}
                    section={section}
                    sourceDataType={sourceDataType}
                    components={components}
                    templateData={templateData}
                    setTemplateData={setTemplateData}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <ToggleButtonGroup
                        color="primary"
                        value={sourceDataType}
                        onChange={handleChange}
                        onClick={(event) => {
                            event.stopPropagation()
                        }}
                        exclusive
                        aria-label="source data type"
                        size="small"
                        sx={{
                            mt: sourceDataType === 'Component Library' ? 0 : 1,
                        }}
                    >
                        {source_data_types.map((source_data_type) => (
                            <ToggleButton
                                key={source_data_type.id}
                                value={source_data_type.name}
                            >
                                <Tooltip
                                    title={formatSourceDataType(
                                        source_data_type
                                    )}
                                >
                                    {source_data_type.icon}
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </ElementContainer>
        </>
    )
}

const TemplateGuidelineSectionContentComponent = ({
    section,
    components,
    templateData,
    setTemplateData,
    addNewElement,
}) => {
    console.log('section: ', section.section_name)
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            {section.fixed_elements.length > 0 && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 'bold' }}
                        >
                            Elements
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={addNewElement}
                            aria-label="add tag"
                            sx={{
                                padding: 0,
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {section.fixed_elements.map((element) => (
                            <Box key={uuidv4()} sx={{ width: '100%' }}>
                                <FixedElementsComponent
                                    element={element}
                                    section={section}
                                    components={components}
                                    templateData={templateData}
                                    setTemplateData={setTemplateData}
                                />
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    )
}

export default TemplateGuidelineSectionContentComponent
